import React, { useState, useEffect, useCallback } from 'react';
import { recordsAPI } from '../services/api';
import { useNavigate } from 'react-router-dom';

const typeColors = {
    A: '#1976d2',
    AAAA: '#7b1fa2',
    CNAME: '#e65100',
    MX: '#2e7d32',
    TXT: '#f9a825',
    NS: '#757575',
};

function GlobalSearch() {
    const [query, setQuery] = useState('');
    const [typeFilter, setTypeFilter] = useState('');
    const [results, setResults] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [searched, setSearched] = useState(false);
    const navigate = useNavigate();

    const performSearch = useCallback(async (q, type) => {
        if (q.length < 2) {
            setResults([]);
            setSearched(false);
            return;
        }

        setLoading(true);
        setError(null);

        try {
            const response = await recordsAPI.search(q, type);
            const data = response.data;
            if (data.success) {
                setResults(data.results);
            } else {
                setError(data.error || 'Erro na busca');
            }
            setSearched(true);
        } catch (err) {
            setError(err.message || 'Erro na busca');
            setSearched(true);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        const timer = setTimeout(() => {
            performSearch(query, typeFilter);
        }, 400);

        return () => clearTimeout(timer);
    }, [query, typeFilter, performSearch]);

    const getBadgeStyle = (type) => {
        const color = typeColors[type] || '#999';
        return {
            display: 'inline-block',
            padding: '2px 8px',
            borderRadius: '4px',
            backgroundColor: color,
            color: '#fff',
            fontSize: '12px',
            fontWeight: 600,
        };
    };

    return (
        <div className="global-search" style={{ padding: '24px' }}>
            <div className="gs-header" style={{ marginBottom: '24px' }}>
                <h2 style={{ margin: 0, color: '#333', fontSize: '24px' }}>Buscar Registros</h2>
                <p style={{ margin: '4px 0 0', color: '#777', fontSize: '14px' }}>
                    Pesquise em todos os dominios
                </p>
            </div>

            <div className="gs-filters" style={{ display: 'flex', gap: '12px', marginBottom: '24px' }}>
                <input
                    type="text"
                    placeholder="Digite pelo menos 2 caracteres..."
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    style={{
                        flex: 1,
                        padding: '10px 14px',
                        border: '1px solid #ddd',
                        borderRadius: '6px',
                        fontSize: '14px',
                        outline: 'none',
                    }}
                />
                <select
                    value={typeFilter}
                    onChange={(e) => setTypeFilter(e.target.value)}
                    style={{
                        padding: '10px 14px',
                        border: '1px solid #ddd',
                        borderRadius: '6px',
                        fontSize: '14px',
                        outline: 'none',
                        minWidth: '120px',
                    }}
                >
                    <option value="">All</option>
                    <option value="A">A</option>
                    <option value="AAAA">AAAA</option>
                    <option value="CNAME">CNAME</option>
                    <option value="MX">MX</option>
                    <option value="TXT">TXT</option>
                    <option value="NS">NS</option>
                </select>
            </div>

            {error && (
                <div style={{ padding: '12px', backgroundColor: '#fdecea', color: '#c62828', borderRadius: '6px', marginBottom: '16px' }}>
                    {error}
                </div>
            )}

            {loading && (
                <div style={{ textAlign: 'center', padding: '40px', color: '#999' }}>
                    Buscando...
                </div>
            )}

            {!loading && searched && results.length === 0 && (
                <div style={{ textAlign: 'center', padding: '40px', color: '#999' }}>
                    Nenhum registro encontrado
                </div>
            )}

            {!loading && (
                <div className="gs-results">
                    {results.map((group) => (
                        <div key={group.zone.id} style={{ marginBottom: '24px', border: '1px solid #e0e0e0', borderRadius: '8px', overflow: 'hidden' }}>
                            <div
                                style={{
                                    padding: '12px 16px',
                                    backgroundColor: '#f5f5f5',
                                    borderBottom: '1px solid #e0e0e0',
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    alignItems: 'center',
                                }}
                            >
                                <span
                                    onClick={() => navigate(`/zones/${group.zone.id}`)}
                                    style={{ fontWeight: 600, color: '#1976d2', cursor: 'pointer', fontSize: '15px' }}
                                >
                                    {group.zone.name}
                                </span>
                                <span style={{ fontSize: '13px', color: '#999' }}>
                                    {group.records.length} registro{group.records.length !== 1 ? 's' : ''}
                                </span>
                            </div>
                            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
                                <thead>
                                    <tr style={{ backgroundColor: '#fafafa' }}>
                                        <th style={{ textAlign: 'left', padding: '8px 16px', borderBottom: '1px solid #eee', fontWeight: 600, color: '#555' }}>Name</th>
                                        <th style={{ textAlign: 'left', padding: '8px 16px', borderBottom: '1px solid #eee', fontWeight: 600, color: '#555' }}>Type</th>
                                        <th style={{ textAlign: 'left', padding: '8px 16px', borderBottom: '1px solid #eee', fontWeight: 600, color: '#555' }}>Content</th>
                                        <th style={{ textAlign: 'left', padding: '8px 16px', borderBottom: '1px solid #eee', fontWeight: 600, color: '#555' }}>TTL</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {group.records.map((record, idx) => (
                                        <tr key={record.id || idx} style={{ backgroundColor: idx % 2 === 0 ? '#fff' : '#fafafa' }}>
                                            <td style={{ padding: '8px 16px', borderBottom: '1px solid #f0f0f0', maxWidth: '250px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                                {record.name}
                                            </td>
                                            <td style={{ padding: '8px 16px', borderBottom: '1px solid #f0f0f0' }}>
                                                <span style={getBadgeStyle(record.type)}>{record.type}</span>
                                            </td>
                                            <td style={{ padding: '8px 16px', borderBottom: '1px solid #f0f0f0', maxWidth: '350px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                                {record.content}
                                            </td>
                                            <td style={{ padding: '8px 16px', borderBottom: '1px solid #f0f0f0', color: '#777' }}>
                                                {record.ttl === 1 ? 'Auto' : record.ttl}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}

export default GlobalSearch;
